import { useState } from "react";
import { Mail, Phone, MapPin, Send, MessageCircle, BadgeCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";
import { useLang } from "@/context/LanguageContext";
import { useSEO } from "@/lib/useSEO";

const ContactPage = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  useSEO({
    title: lang === "en" ? "Contact us" : "Contatti",
    description: lang === "en"
      ? "Get in touch with La dimora di Patty. WhatsApp, email or the contact form — we reply within 24 hours."
      : "Contatta La dimora di Patty. WhatsApp, email o modulo contatti — rispondiamo entro 24 ore.",
  });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries", form);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error(t("contact.error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal className="text-center mb-16">
          <div className="overline text-accent gold-line-center mb-6">{t("contact.page_overline")}</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-tight">
            {t("contact.page_title")}
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto mt-6">
            {t("contact.page_subtitle")}
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5 space-y-6">
            <ContactCard icon={MapPin} title={t("contact.where")} lines={["La dimora di Patty", "Via Numicia 40", "67031 Castel di Sangro (AQ)", "Abruzzo, Italia"]} />
            <ContactCard icon={Phone} title={t("contact.phone")} lines={["+39 379 29 23 929"]} />
            <ContactCard icon={Mail} title={t("contact.email")} lines={["ospiti@ladimoradipatty.it"]} />
            <ContactCard icon={BadgeCheck} title={t("contact.cin")} lines={["IT066028C2GFSS7WQS"]} />
            <a
              data-testid="contact-whatsapp"
              href="https://wa.me/393792923929"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-6 bg-card border border-border rounded-2xl hover:border-accent transition-colors"
            >
              <div className="w-12 h-12 rounded-full bg-[#25D366]/10 flex items-center justify-center">
                <MessageCircle size={20} className="text-[#25D366]" />
              </div>
              <div>
                <div className="overline text-accent">{t("contact.whatsapp_overline")}</div>
                <div className="font-serif text-xl mt-1">{t("contact.whatsapp_title")}</div>
              </div>
            </a>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={150}>
            <form onSubmit={submit} className="bg-card border border-border rounded-2xl p-8 space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cname">{t("contact.form_name")} *</Label>
                  <Input data-testid="contact-name" id="cname" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2" />
                </div>
                <div>
                  <Label htmlFor="cemail">{t("contact.form_email")} *</Label>
                  <Input data-testid="contact-email" id="cemail" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cphone">{t("contact.form_phone")}</Label>
                  <Input id="cphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2" />
                </div>
                <div>
                  <Label htmlFor="csubj">{t("contact.form_subject")}</Label>
                  <Input id="csubj" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-2" />
                </div>
              </div>
              <div>
                <Label htmlFor="cmsg">{t("contact.form_message")} *</Label>
                <Textarea data-testid="contact-message" id="cmsg" rows={6} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2" />
              </div>
              <button
                data-testid="contact-submit"
                disabled={sending}
                className="btn-shimmer w-full py-4 rounded-full bg-foreground text-background hover:bg-accent transition-colors disabled:opacity-50 text-sm tracking-wide flex items-center justify-center gap-2"
              >
                <Send size={16} /> {sending ? t("contact.form_sending") : t("contact.form_submit")}
              </button>
            </form>
          </Reveal>
        </div>

        <Reveal className="mt-16" delay={100}>
          <div className="text-center mb-8">
            <div className="overline text-accent gold-line-center mb-4">{t("contact.map_overline")}</div>
            <h2 className="font-serif text-3xl sm:text-4xl">{t("contact.map_title")}</h2>
          </div>
          <div data-testid="contact-map" className="rounded-2xl overflow-hidden border border-border shadow-sm">
            <iframe
              title="La dimora di Patty — Via Numicia 40, Castel di Sangro"
              src="https://www.google.com/maps?q=Via+Numicia+40,+67031+Castel+di+Sangro+AQ,+Italia&output=embed&z=16"
              width="100%"
              height="420"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <div className="text-center mt-6">
            <a
              data-testid="contact-map-directions"
              href="https://www.google.com/maps/dir/?api=1&destination=Via+Numicia+40,+67031+Castel+di+Sangro+AQ,+Italia"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full border border-accent text-accent hover:bg-accent hover:text-background transition-colors text-sm tracking-wide"
            >
              <MapPin size={16} /> {t("contact.map_directions")}
            </a>
          </div>
        </Reveal>
      </div>
    </main>
  );
};

const ContactCard = ({ icon: Icon, title, lines }) => (
  <div className="flex items-start gap-4 p-6 bg-card border border-border rounded-2xl">
    <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
      <Icon size={20} className="text-accent" />
    </div>
    <div>
      <div className="overline text-accent">{title}</div>
      {lines.map((l, i) => (
        <div key={i} className="font-serif text-xl mt-1">{l}</div>
      ))}
    </div>
  </div>
);

export default ContactPage;
